import { useState, useEffect, useContext, useRef } from 'react';
import { useParams } from 'react-router-dom';

import { Window } from '@progress/kendo-react-dialogs';
import { Form, Field, FormElement, FormRenderProps } from "@progress/kendo-react-form";
import { MaskedTextBox, MaskedTextBoxProps } from '@progress/kendo-react-inputs';
import { GridLayout, GridLayoutItem, TabStrip, TabStripTab } from '@progress/kendo-react-layout';
import { Button } from '@progress/kendo-react-buttons';
import { Popup } from "@progress/kendo-react-popup";

import { SetValueOnObject } from '../../utils/UnsafeTypescriptUtil';
import { SubmittedKit, blankSubmittedKit, apiModelToSubmittedKit } from '../../models/SubmittedKit';
import { protectedResources } from '../../authConfig';
import { useFetchWithMsal } from '../../hooks/useFetchWithMsal';
import SubmittedKitRunList from '../submittedKitRun/SubmittedKitRun_List';
import ManageSubmittedKitRun from '../submittedKitRun/SubmittedKitRun_Manage';
import { LoadPage } from '../../components/LoadPage';
import { StreckDropDownTypes, TypedStreckDropDown } from '../../components/TypedStreckDropDown';
import { AppUserContext } from '../../components/PvsContextProvider';
import { dateInputLabel, largeKitSizeValidator, NumericInputLabel, smallKitSizeValidator } from "../../components/Validators";
import { useNotificationAPI } from '../../components/NotificationProvider';
import { kitSizes, padKitNumber, padLotNumber } from '../../models/kit';
import GenerateLegacyKitSerialNumber from './GenerateLegacyKitSerialNumber';
import { KitStatus, Status, kitStatuses } from '../../models/kitStatus';
import { SerialPage } from '../serial/SerialPage';
import { SubmittedKitRun } from '../../models/SubmittedKitRun';
import { AddComment } from '../Comments/AddCommentBox';
import { CommentList } from '../Comments/Comment_List';
import { KitRunStatus } from '../../models/kitRunStatus';
import { loginPrompt } from '../../utils/loginPrompt';
import ReportList from '../reports/Report_List';
import { getReportURL } from '../../utils/reportUtil';
import { SubmittedKitTramLine } from './submittedKitTramLine';
import { EmailReportToList } from './EmailReportToList';
import { ObjectTypes } from '../../models/comments';

interface ManageSubmittedKitProps {
    labId?: number,
    children?: React.ReactNode
}

const windowHeight = 300;
const windowWidth = 400;

const pageTitle = 'Submitted Kit Manager';

const ManageSubmittedKit = (props: ManageSubmittedKitProps) => {
    const params = useParams();
    const appUserContext = useContext(AppUserContext);
    const { addNotification, addNotifications } = useNotificationAPI();

    const { isLoading, error, execute } = useFetchWithMsal({
        scopes: protectedResources.scopes.admin
    });

    const [waitingForConfirmation, setWaitingForConfirmation] = useState(false);
    const showLoadingWheel = () => { return isLoading || waitingForConfirmation }

    const [submittedKitRunListKey, setSubmittedKitRunListKey] = useState(Date.now());
    const [commentListKey, setCommnetListKey] = useState(Date.now());

    const [selectedTab, setSelectedTab] = useState(0);
    const [showAddComment, setShowAddComment] = useState(false);
    const [showRerunComment, setShowRerunComment] = useState(false);
    const [showUnlockKitComment, setShowUnlockKitComment] = useState(false);
    const [showBackComment, setShowBackComment] = useState(false);
    const [showErrorComment, setShowErrorComment] = useState(false);
    const [showGenerateLegacyKit, setShowGenerateLegacyKit] = useState(false);
    const [showAddSubmittedKitRun, setShowAddSubmittedKitRun] = useState(false);
    const barcodeTextbox = useRef<MaskedTextBox | null>(null);
    const [showBarcodeText, setShowBarcodeText] = useState(false);
    const barcodeAnchor = useRef<HTMLSpanElement | null>(null);
    const [barcodeNumber, setBarcodeNumber] = useState("");
    const [emailList, setEmailList] = useState("");
    const [requireConfirmation, setRequireConfirmation] = useState(false);
    const [confirmationBarcodeNumber, setConfirmationBarcodeNumber] = useState("");
    const [streckOperator, _setStreckOperator] = useState("");
    const [validStreckOperator, setValidStreckOperator] = useState(true);
    const setStreckOperator = (value: string) => {
        if (value === "___") {
            _setStreckOperator("");
            setValidStreckOperator(true);
        } else {
            _setStreckOperator(value);
            setValidStreckOperator(value.length === 0 || value.match(/[a-zA-Z0-9]{3}/)?.length === 1);
        }
    }

    let intialSubmittedKit = structuredClone(blankSubmittedKit);
    if (params.SubmittedKitId !== undefined) {
        intialSubmittedKit = SetValueOnObject(intialSubmittedKit, "Id", params.SubmittedKitId) as SubmittedKit;
    }
    if (props.labId) {
        intialSubmittedKit = SetValueOnObject(intialSubmittedKit, "LabID", props.labId) as SubmittedKit;
    }
    const [submittedKit, setSubmittedKit] = useState(intialSubmittedKit);
    const [currentStatusId, setCurrentStatusId] = useState(0);

    const onChange_SubmittedKit = (e: any, propName: string) => {
        let updatedSubmittedKit: SubmittedKit = SetValueOnObject(submittedKit, propName, e.target.value) as SubmittedKit;
        setSubmittedKit(updatedSubmittedKit);
    }

    const handleDropDownListChange = (id: number, propName: string) => {
        let updatedSubmittedKit: SubmittedKit = SetValueOnObject(submittedKit, propName, id) as SubmittedKit;
        setSubmittedKit(updatedSubmittedKit);
    }

    const getComponentSerialNumberParts = () => {
        const splitSerialNumber = barcodeNumber.split("-");
        return {
            LotNumber: Number.parseInt(splitSerialNumber[0]),
            KitVialSize: splitSerialNumber[1],
            KitNumber: Number.parseInt(splitSerialNumber[2])
        };
    }

    const kitVialSizeToInt = (kitVialSize: string) => {
        return (kitVialSize === "L" ? 2 : 1);
    }

    //LabId change is handled differently because we need React to recognize the change right away 
    //  so LabId is passed into the 'Add a *' Modals correctly
    const labIdChange = (id: number) => {
        setSubmittedKit({
            Id: submittedKit.Id,
            LabID: id,
            OperatorId: submittedKit.OperatorId,
            StreckOperator: submittedKit.StreckOperator,
            LotNumber: submittedKit.LotNumber,
            KitVialSize: submittedKit.KitVialSize,
            KitNumber: submittedKit.KitNumber,
            PipetID: submittedKit.PipetID,
            PipetDeliveryVolume: submittedKit.PipetDeliveryVolume,
            UM: submittedKit.UM,
            RefractometerSerialNumber: "",
            WaterVolume: submittedKit.WaterVolume,
            ValuesUsed: submittedKit.ValuesUsed,
            Mean: submittedKit.Mean,
            MeanPCT: submittedKit.MeanPCT,
            SD: submittedKit.SD,
            CV: submittedKit.CV,
            Locked: submittedKit.Locked,
            Review: submittedKit.Review,
            DateEntered: submittedKit.DateEntered,
        } as SubmittedKit);
    }

    const submitKitRun = (value: string) => {
        let fValue = Number.parseFloat(value);
        if (!fValue) {
            return;
        }
        execute('POST', protectedResources.apiSubmittedKitRun.endpoint + "CreateSubmittedKitRun", {
            Id: 0,
            SubmittedKitId: submittedKit.Id,
            PipetVolume: fValue,
            Measurement: fValue,
            RunDate: new Date(),
            KitRunStatusId: KitRunStatus.Included,
            Comments: ''
        } as SubmittedKitRun)
            .then(((result: any) => {
                if (result?.id) {
                    addNotification("Run Added Successfully to Kit " + submittedKit.Barcode, pageTitle, "success");
                    getSubmittedKit();
                    setSubmittedKitRunListKey(Date.now());
                }
            }),
            );
    };

    const submitKit = (formRenderProps: FormRenderProps) => {
        if (!formRenderProps.valid || !validStreckOperator) {
            return;
        }

        if (requireConfirmation && barcodeNumber !== confirmationBarcodeNumber) {
            addNotification("The two barcode numbers don't match. Please check again.", pageTitle, "info");
            return;
        }

        const apiEndpoint = submittedKit.Id > 0 ?
            protectedResources.apiSubmittedKit.endpoint + "UpdateSubmittedKit" :
            protectedResources.apiSubmittedKit.endpoint + "CreateSubmittedKit";

        const splitSerialNumber = getComponentSerialNumberParts();

        execute(submittedKit.Id > 0 ? 'PUT' : 'POST', apiEndpoint, {
            Id: submittedKit.Id,
            LabID: submittedKit.LabID,
            OperatorId: submittedKit.OperatorId,
            StreckOperator: (streckOperator === "___" ? "" : streckOperator),
            LotNumber: splitSerialNumber.LotNumber,
            KitVialSize: kitVialSizeToInt(splitSerialNumber.KitVialSize),
            KitNumber: splitSerialNumber.KitNumber,
            PipetID: submittedKit.PipetID,
            PipetDeliveryVolume: submittedKit.PipetDeliveryVolume,
            UM: submittedKit.UM,
            RefractometerSerialNumber: "",
            EmailReportTo: emailList ? emailList : submittedKit.EmailReportTo,
            WaterVolume: submittedKit.WaterVolume,
            ValuesUsed: submittedKit.ValuesUsed,
            Mean: submittedKit.Mean,
            MeanPCT: submittedKit.MeanPCT,
            SD: submittedKit.SD,
            CV: submittedKit.CV,
            Locked: submittedKit.Locked,
            Review: submittedKit.Review,
            DateEntered: submittedKit.DateEntered.toLocaleDateString('en-us', { year: 'numeric', day: '2-digit', month: '2-digit' }),
            OverRideKitInUseWarning: requireConfirmation
        }).then(
            ((result: any) => {
                if (result?.id) {
                    if (submittedKit.Id === 0) {
                        setCurrentStatusId(Status.CreatedStatus.id);
                        window.history.replaceState(null, '', '/submitted-kit-manage/' + result?.id);
                    }
                    setRequireConfirmation(false);
                    addNotification('Submitted Kit ' + submittedKit.Barcode + (submittedKit.Id > 0 ? ' updated' : ' created') + ': ' + barcodeNumber, pageTitle, 'success');
                    setSubmittedKit(apiModelToSubmittedKit(result));
                }
            }),
        );
    };

    const getSubmittedKit = () => {
        if (submittedKit.Id > 0) {
            return execute('GET', protectedResources.apiSubmittedKit.endpoint + "GetSubmittedKit/?Id=" + submittedKit.Id)
                .then(
                    ((result: any) => {
                        if (result) {
                            let model = apiModelToSubmittedKit(result);
                            setSubmittedKit(model);
                            setBarcodeNumber(padLotNumber(model.LotNumber) + '-' + kitSizes[model.KitVialSize].shortText + '-' + padKitNumber(model.KitNumber));
                            setStreckOperator(model.StreckOperator);
                            setCurrentStatusId(model.KitStatus.kitStatusId);
                        }
                    })
                );
        } else {
            setSubmittedKit(structuredClone(blankSubmittedKit));
            const element = barcodeTextbox.current;
            element?.focus();
        }
        return null;
    }

    const setKitToNextStatus = () => {
        setWaitingForConfirmation(true);
        const thenFunction = (result: any) => {
            setWaitingForConfirmation(false);
            if (!result) return;
            if (Number.isInteger(result.kitStatus)) {
                setCurrentStatusId(result.kitStatus);
                if (result.submittedKit) {
                    let model = apiModelToSubmittedKit(result.submittedKit);
                    setSubmittedKit(model);
                }
                setSubmittedKitRunListKey(Date.now());
            }
        }

        const authFailedFunction = () => {
            setWaitingForConfirmation(false);
        }

        if (currentStatusId === KitStatus.Processing && (submittedKit.ValuesUsed === undefined || submittedKit.ValuesUsed > 6)) {
            //This error messaged is duplicated in SubmittedKitManager. If you change it here change it there
            addNotification("There are " + submittedKit.ValuesUsed + " runs included on this kit " + submittedKit.Barcode + ". Please exclude runs until there are 6 or less.", pageTitle, "warning");
            return;
        }

        if (currentStatusId === Status.ReceivedStatus.id || currentStatusId === Status.ProcessingStatus.id) {
            loginPrompt('POST', protectedResources.apiSubmittedKit.endpoint + 'AdvanceSubmittedKit', { Id: submittedKit.Id }, addNotifications, thenFunction, authFailedFunction);
        } else if (currentStatusId === Status.ReviewStatus.id) {
            loginPrompt('POST', protectedResources.apiSubmittedKit.endpoint + 'AdvanceSubmittedKitPastReview', { Id: submittedKit.Id }, addNotifications, thenFunction, authFailedFunction);
        } else {
            execute('POST', protectedResources.apiSubmittedKit.endpoint + 'AdvanceSubmittedKit', { Id: submittedKit.Id })
                .then(
                    thenFunction
                )
        }
    }

    const showLatestReport = () => {
        const reportURLFunction = (reportUrl: string) => {
            window.open(reportUrl);
        };
        const blobIsEmptyOrUndefined = () => {
            if (appUserContext.isStreckAdmin) {
                addNotification("There was an error opening the report for this kit " + submittedKit.Barcode + ". Please check the Report tab, and generate a report if none are present.", pageTitle, "info");
            } else {
                addNotification("There was an error opening the report for this kit " + submittedKit.Barcode + ". Please contact STATS to resolve this issue.", pageTitle, "info");
            }
        };

        getReportURL(execute, protectedResources.apiReport.endpoint + "GetDocumentBySubmittedKitId?Id=" + submittedKit.Id, reportURLFunction, blobIsEmptyOrUndefined);
    }

    const afterRerunKit = (result: any) => {
        addNotification("Kit Runs excluded on Kit " + submittedKit.Barcode, pageTitle, "success");
        setSubmittedKit(apiModelToSubmittedKit(result));
        setCurrentStatusId(KitStatus.Processing);
        setSubmittedKitRunListKey(Date.now());
        setShowRerunComment(false);
    }

    const afterErrorKit = (result: any) => {
        setSubmittedKit(apiModelToSubmittedKit(result));
        setCurrentStatusId(KitStatus.Error);
        setCommnetListKey(Date.now());
        setShowErrorComment(false);
    }

    const statusCheck = (s: KitStatus[]) => {
        let result = false
        s.forEach(stat => {
            if (currentStatusId === stat) {
                result = true
            }
        })
        return result
    }

    const backButtonDisplay = () => {
        if (appUserContext.isStreckAdmin && statusCheck([KitStatus.Received, KitStatus.Processing, KitStatus.Review])) {
            return 'inherit';
        }
        return 'none';
    }
    const disableSubmitButton = () => {
        return (currentStatusId === KitStatus.Error || submittedKit.Locked || appUserContext.isReadOnly) && appUserContext.isStreckAdmin;
    }
    const nextButtonDisplay = () => {
        if (appUserContext.isStreckAdmin && submittedKit.Id > 0 && statusCheck([KitStatus.Created, KitStatus.Received, KitStatus.Processing, KitStatus.Review])) {
            return 'inherit';
        }
        return 'none';
    }
    const rerunButtonDisplay = () => {
        if (appUserContext.isStreckAdmin && statusCheck([KitStatus.Processing, KitStatus.Completed, KitStatus.Review])) {
            return 'inherit';
        }
        return 'none';
    }
    const errorButtonDisplay = () => {
        if (appUserContext.isStreckAdmin && submittedKit.Id > 0 && currentStatusId !== KitStatus.Error) {
            return 'inherit';
        }
        return 'none';
    }

    const closeAddSubmittedKitRunWindow = () => {
        addNotification("Added Kit Run for " + barcodeNumber, pageTitle, "success");
        getSubmittedKit();
        setSubmittedKitRunListKey(Date.now());
    }

    const navigateToTab = (e: any) => {
        setSelectedTab(e.selected);
    }

    useEffect(() => {
        if (error) {
            if (error?.errors?.findIndex((x: any) => x.code === "BarcodeNumberInUse") >= 0) {
                setRequireConfirmation(true);
                setConfirmationBarcodeNumber("");
            }
        }
    }, [error]);

    useEffect(() => {
        getSubmittedKit();
    }, [execute]);

    //#region Doesnt allow pasting into Barcode Confirmation Textbox
    useEffect(() => {
        let input = document.getElementById("ConfirmBarCode")
        let barcode = (e: ClipboardEvent) => {
            e.preventDefault()
        }
        if (input !== null) {
            input.onpaste = barcode;
        }
    }, [requireConfirmation])
    //#endregion
    return <>
        {appUserContext.adminView && submittedKit.Id > 0 && (
            <SubmittedKitTramLine review={submittedKit.Review} kitStatusId={currentStatusId} />
        )}

        {showGenerateLegacyKit &&
            <Window title={'Generate Barcode'} onClose={() => { setShowGenerateLegacyKit(false); }} initialWidth={320}>
                <GenerateLegacyKitSerialNumber setSerialNumber={setBarcodeNumber} closeWindow={() => setShowGenerateLegacyKit(false)}></GenerateLegacyKitSerialNumber>
            </Window>}
        {showAddSubmittedKitRun &&
            <Window title={'Create Kit Run'} onClose={() => { setShowAddSubmittedKitRun(false); }} initialHeight={windowHeight} initialWidth={windowWidth}>
                <ManageSubmittedKitRun closeWindow={closeAddSubmittedKitRunWindow} submittedKitId={submittedKit.Id}></ManageSubmittedKitRun>
            </Window>}
        {showAddComment &&
            <Window title={'Add Comment'} onClose={() => { setShowAddComment(false); }} initialHeight={windowHeight} initialWidth={windowWidth}>
                <AddComment
                    loginPrompt={false}
                    objectId={submittedKit.Id} objectTypeId={ObjectTypes.SubmittedKit}
                    buttonText="Add Comment" header="Add comment to kit"
                    endpoint={protectedResources.apiComment.endpoint + "CreateComment"}
                    afterAddFunction={() => { setShowAddComment(false); setCommnetListKey(Date.now()); }} execute={execute} ></AddComment>
            </Window>}
        {showErrorComment &&
            <Window title={'Set Kit to Error'} onClose={() => { setShowErrorComment(false); }} initialHeight={windowHeight} initialWidth={windowWidth}>
                <AddComment
                    loginPrompt={true}
                    objectId={submittedKit.Id}
                    buttonText="Set Kit to Error" header="Enter the reason the kit is erroneous"
                    endpoint={protectedResources.apiSubmittedKit.endpoint + "SetKitToError"}
                    afterAddFunction={(result: any) => { afterErrorKit(result); }} execute={execute}
                    setLoading={setWaitingForConfirmation}></AddComment>
            </Window>}
        {showUnlockKitComment &&
            <Window title={'Unlock Submitted Kit'} onClose={() => { setShowUnlockKitComment(false); }} initialHeight={windowHeight} initialWidth={windowWidth}>
                <AddComment loginPrompt={true}
                    objectId={submittedKit.Id}
                    buttonText="Unlock Submitted Kit" header="Enter the reason to unlock the kit"
                    endpoint={protectedResources.apiSubmittedKit.endpoint + "UnlockSubmittedKit"}
                    afterAddFunction={(result: any) => { setSubmittedKit(apiModelToSubmittedKit(result)); setShowUnlockKitComment(false); }} execute={execute}
                    setLoading={setWaitingForConfirmation}></AddComment>
            </Window>}
        {showRerunComment &&
            <Window title={'Rerun Submitted Kit'} onClose={() => { setShowRerunComment(false); }} initialHeight={windowHeight} initialWidth={windowWidth}>
                <AddComment loginPrompt={true}
                    objectId={submittedKit.Id}
                    buttonText="Rerun Submitted Kit" header="Enter the reason to re run the kit"
                    endpoint={protectedResources.apiSubmittedKit.endpoint + "RerunKit"} afterAddFunction={(result: any) => { afterRerunKit(result); }} execute={execute}
                    setLoading={setWaitingForConfirmation}></AddComment>
            </Window>}
        {showBackComment &&
            <Window title={'Send Kit Back'} onClose={() => { setShowBackComment(false); }} initialHeight={windowHeight} initialWidth={windowWidth}>
                <AddComment loginPrompt={true}
                    objectId={submittedKit.Id}
                    buttonText="Send Kit Back" header="Enter the reason the kit is being sent back"
                    endpoint={protectedResources.apiSubmittedKit.endpoint + "SendBackSubmittedKit"}
                    afterAddFunction={() => { setCurrentStatusId(currentStatusId - 1); setShowBackComment(false); }} execute={execute}
                    setLoading={setWaitingForConfirmation}></AddComment>
            </Window>}

        {appUserContext.isStreckAdmin && submittedKit.Id === 0 &&
            <div className="mb-3 col">
                <Button onClick={() => setShowGenerateLegacyKit(true)}>Generate Legacy Barcode</Button>
            </div>}

        <LoadPage isLoading={showLoadingWheel()}>
            <GridLayout cols={[{ width: "300px" }, { width: "auto" }]} gap={{ cols: 15, rows: 15 }}>
                <GridLayoutItem row={1} col={1}>
                    <Form initialValues={submittedKit} key={JSON.stringify(submittedKit)} render={(formRenderProps) =>
                        <FormElement className='s-k-flex-50 p-4 rounded-lg streck-secondary'>
                            <fieldset className={'k-form-fieldset'}>
                                <legend className={'k-form-legend'}>{submittedKit.Locked ? <span style={{ color: "red" }}>This kit is Locked. <br></br>Click the lock button to unlock.</span> : <>Please fill in the fields:</>}</legend>
                                <div className="col">
                                    <div className="row">
                                        Barcode Number:
                                        {submittedKit.Id > 0 ?
                                            <>
                                                {submittedKit.Locked ?
                                                    <Button onClick={() => { setShowUnlockKitComment(true); }} className="submitted-kit-buttons" style={{ left: "30px" }} disabled={!appUserContext.isStreckAdmin}>
                                                        <>Locked <span className="k-icon k-font-icon k-i-lock"></span></>
                                                    </Button> :
                                                    <Button disabled={disableSubmitButton()} onClick={() => submitKit(formRenderProps)} type={"submit"} className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-base submitted-kit-buttons" style={{ left: "3px" }}>
                                                        Save Changes
                                                    </Button>}
                                            </> :
                                            <>
                                                &nbsp;&nbsp;
                                                <span ref={barcodeAnchor} onClick={() => setShowBarcodeText(!showBarcodeText)}>
                                                    {showBarcodeText ?
                                                        <span className="k-icon k-i-close" style={{ color: "red" }}></span> :
                                                        <span className="k-icon k-i-info"></span>}
                                                </span>
                                                <Popup anchor={barcodeAnchor.current} show={showBarcodeText} popupClass={"popup-content}"}>
                                                    <span style={{ margin: "3px" }}>
                                                        The barcode is 4 numbers, follwed by a S or an L, follwed by 5 more numbers.
                                                        <br></br><br></br>
                                                        An Example Barcode: 0000-S-00000
                                                        <br></br><br></br>
                                                        Please view our FAQ if you have more questions.
                                                    </span>
                                                </Popup>
                                            </>}
                                        <br></br>
                                        <MaskedTextBox ref={barcodeTextbox} disabled={submittedKit.Locked} mask="0000-T-00000" defaultValue="" rules={{ "T": /(s|l|S|L)/ }} value={barcodeNumber.toUpperCase()} onChange={(e: MaskedTextBoxProps) => { setBarcodeNumber(e.value as string) }}></MaskedTextBox>
                                        {requireConfirmation &&
                                            <>
                                                <span>Please enter your barcode again.</span>
                                                <MaskedTextBox id="ConfirmBarCode" mask="0000-T-00000" defaultValue="" rules={{ "T": /(s|l|S|L)/ }} value={confirmationBarcodeNumber.toUpperCase()} onChange={(e: MaskedTextBoxProps) => { setConfirmationBarcodeNumber(e.value as string) }}></MaskedTextBox>
                                            </>}
                                    </div>
                                    <div className="row">
                                        <TypedStreckDropDown
                                            streckDropDownType={StreckDropDownTypes.Lab}
                                            handleChange={(result: any) => labIdChange(result)}
                                            selectedId={submittedKit.LabID}
                                            required={true}
                                            disabled={submittedKit.Locked}
                                        ></TypedStreckDropDown>
                                    </div>
                                    <div className="row">
                                        <TypedStreckDropDown
                                            streckDropDownType={StreckDropDownTypes.Pipet}
                                            handleChange={(id) => handleDropDownListChange(id, "PipetID")}
                                            selectedId={submittedKit.PipetID}
                                            selectedObject={submittedKit.pipet}
                                            labId={submittedKit.LabID}
                                            required={true}
                                            disabled={submittedKit.Locked}
                                        ></TypedStreckDropDown>
                                    </div>
                                    <div className="row">
                                        <TypedStreckDropDown
                                            streckDropDownType={StreckDropDownTypes.Operator}
                                            handleChange={(id) => handleDropDownListChange(id, "OperatorId")}
                                            selectedId={submittedKit.OperatorId}
                                            selectedObject={submittedKit.operator}
                                            labId={submittedKit.LabID}
                                            required={true}
                                            disabled={submittedKit.Locked}
                                        />
                                    </div>
                                    <div className="row">
                                        <Field name={'PipetDeliveryVolume'} label={"Pipet Delivery Volume(ul)*"} component={NumericInputLabel} validator={getComponentSerialNumberParts().KitVialSize === "S" ? smallKitSizeValidator : largeKitSizeValidator} onChange={(event) => onChange_SubmittedKit(event, "PipetDeliveryVolume")} disabled={submittedKit.Locked} />
                                    </div>
                                    <div className="row">
                                        <Field name={"DateEntered"} component={dateInputLabel} label={"Date Entered*"} required={true} onChange={(event) => onChange_SubmittedKit(event, "DateEntered")} disabled={submittedKit.Locked} />
                                    </div>
                                </div>
                                <div className="k-form-buttons d-flex justify-content-center">
                                    <Button style={{ display: backButtonDisplay() }} onSubmit={(event) => event.preventDefault()} onClick={() => setShowBackComment(true)} type={"button"} className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-base">
                                        Back
                                    </Button>
                                    {submittedKit.Id === 0 &&
                                        <Button disabled={disableSubmitButton()} onClick={() => submitKit(formRenderProps)} type={"button"} className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-base">
                                            Create Kit
                                        </Button>}
                                    <Button style={{ display: nextButtonDisplay() }} onClick={() => setKitToNextStatus()} type={"button"} className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-base">
                                        Next
                                    </Button>
                                </div>
                                <div className="k-form-buttons d-flex justify-content-center">
                                    <Button style={{ display: rerunButtonDisplay() }} onClick={() => setShowRerunComment(true)} type={"button"} className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-base">
                                        Rerun Kit
                                    </Button>
                                    <Button style={{ display: errorButtonDisplay() }} onClick={() => setShowErrorComment(true)} type={"button"} className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-base">
                                        Set Kit To Error
                                    </Button>
                                </div>
                            </fieldset>
                        </FormElement>} />
                </GridLayoutItem>

                <GridLayoutItem row={1} col={2}>
                    {submittedKit.Id > 0 ?
                        <>
                            {appUserContext.adminView &&
                                <>
                                    <TabStrip selected={selectedTab} onSelect={navigateToTab}>
                                        <TabStripTab title="Run List">
                                            <span style={{ minWidth: "850px", display: "block" }} >
                                                <SubmittedKitRunList
                                                    id={submittedKit.Id}
                                                    key={submittedKitRunListKey}
                                                    hideStatusColumn={false}/*{currentStatusId() < Status.ReviewStatus.id}*/
                                                    submittedKitRunVolume={submittedKit.PipetDeliveryVolume}>
                                                </SubmittedKitRunList>
                                            </span>
                                        </TabStripTab>
                                        {currentStatusId === KitStatus.Processing &&
                                            <TabStripTab title="Add Run Manually">
                                                <ManageSubmittedKitRun closeWindow={closeAddSubmittedKitRunWindow} submittedKitId={submittedKit.Id}></ManageSubmittedKitRun>
                                            </TabStripTab>}
                                        <TabStripTab title="Comments">
                                            <CommentList
                                                execute={execute} isLoading={isLoading}
                                                endpoint={protectedResources.apiComment.endpoint + "GetAllCommentsBySubmittedKitId?submittedKitId=" + submittedKit.Id}
                                                commentTypeLink={true}
                                                key={commentListKey}></CommentList>
                                            <Button hidden={appUserContext.isReadOnly} onClick={() => setShowAddComment(true)}>Add Comment</Button>
                                        </TabStripTab>
                                        <TabStripTab title="Reports">
                                            <ReportList
                                                submittedKitId={submittedKit.Id}>
                                            </ReportList>
                                        </TabStripTab>
                                        <TabStripTab title="Email To List">
                                            <EmailReportToList
                                                intialList={submittedKit.EmailReportTo}
                                                locked={submittedKit.Locked}
                                                setEmailList={(result: string) => {
                                                    setEmailList(result);
                                                }} ></EmailReportToList>
                                        </TabStripTab>
                                    </TabStrip>

                                    <br></br>
                                </>}
                            {appUserContext.adminView ?
                                <>
                                    <div className="header-info-row s-k-flex-50 p-4 rounded-lg streck-secondary">
                                        <span className="header-info-cell">
                                            <span className="header-info-title">Count:</span><br></br>
                                            <b>&emsp;{submittedKit.ValuesUsed}</b>
                                        </span>
                                        <span className="header-info-cell">
                                            <span className="header-info-title">Mean:</span><br></br>
                                            <b>&emsp;{submittedKit.Mean}</b>
                                        </span>
                                        <span className="header-info-cell">
                                            <span className="header-info-title">Mean %:</span><br></br>
                                            <b>&emsp;{submittedKit.MeanPCT}</b>
                                        </span>
                                        <span className="header-info-cell">
                                            <span className="header-info-title">SD:</span><br></br>
                                            <b>&emsp;{submittedKit.SD}</b>
                                        </span>
                                        <span className="header-info-cell">
                                            <span className="header-info-title">CV:</span><br></br>
                                            <b>&emsp;{submittedKit.CV}</b>
                                        </span>
                                    </div>
                                    {currentStatusId === Status.ProcessingStatus.id && !appUserContext.isReadOnly &&
                                        <>
                                            <br></br><br></br>
                                            <SerialPage showMessages={false} handleCompletedMessage={submitKitRun}></SerialPage>
                                        </>}
                                    {currentStatusId > Status.ProcessingStatus.id && currentStatusId !== Status.ErrorStatus.id &&
                                        <>
                                            <br></br><br></br>
                                            <Button onClick={showLatestReport}>View Latest Report</Button>
                                        </>}
                                </> :
                                <>
                                    <div className="header-info-row s-k-flex-50 p-4 rounded-lg streck-secondary">
                                        <span className="header-info-cell">
                                            <span className="header-info-title">Status:</span><br></br>
                                            <b>&emsp;{currentStatusId > 1 ?
                                                kitStatuses[currentStatusId - 1].memberName :
                                                kitStatuses[0].memberName}
                                            </b>
                                        </span>
                                    </div>
                                    {currentStatusId === Status.EmailSentStatus.id &&
                                        <>
                                            <br></br><br></br>
                                            <Button onClick={showLatestReport}>View Latest Report</Button>
                                        </>}
                                    <br></br><br></br>
                                    <EmailReportToList
                                        intialList={submittedKit.EmailReportTo}
                                        locked={submittedKit.Locked}
                                        setEmailList={(result: string) => {
                                            setEmailList(result);
                                        }} ></EmailReportToList>
                                </>}
                        </> :
                        <>
                            <EmailReportToList
                                intialList={""}
                                locked={false}
                                newKit={true}
                                setEmailList={(result: string) => {
                                    setEmailList(result);
                                }} ></EmailReportToList>
                        </>
                    }
                </GridLayoutItem>
            </GridLayout>
        </LoadPage>
    </>;
};

export default ManageSubmittedKit;